import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface CropVariety {
  id: string;
  cropTypeId: string;
  cropTypeName: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  // Lifecycle Defaults (all optional)
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  /** Variety-level override of the parent CropType's StockPercent. Null = inherit. */
  stockPercent?: number;
  /** Resolved stock split: variety override → crop type default → 70. */
  effectiveStockPercent?: number;
  /** 100 - effectiveStockPercent. */
  effectiveFillingReservePercent?: number;
}

export interface CreateCropVarietyDto {
  cropTypeId: string;
  name: string;
  description?: string;
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  stockPercent?: number;
}

export interface UpdateCropVarietyDto {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  stockPercent?: number;
}

export type CropVarietySortBy = "name" | "cropType" | "createdAt";

export interface CropVarietyListQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: CropVarietySortBy;
  sortDirection?: "asc" | "desc";
  isActive?: boolean;
  cropTypeId?: string;
}

export interface CropVarietyListResult {
  items: CropVariety[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const cropVarietiesApi = {
  getCropVarietiesPaged: async (
    query: CropVarietyListQuery = {}
  ): Promise<CropVarietyListResult> => {
    const params = new URLSearchParams();
    if (query.pageNumber != null)
      params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    if (query.sortBy) params.set("sortBy", query.sortBy);
    if (query.sortDirection) params.set("sortDirection", query.sortDirection);
    if (query.isActive != null)
      params.set("isActive", String(query.isActive));
    if (query.cropTypeId) params.set("cropTypeId", query.cropTypeId);
    const qs = params.toString();
    const { data } = await apiClient.get<ApiResponse<CropVarietyListResult>>(
      qs ? `/crop-varieties/paged?${qs}` : "/crop-varieties/paged"
    );
    return data.data;
  },

  getAllCropVarieties: async (): Promise<CropVariety[]> => {
    const { data } = await apiClient.get<ApiResponse<CropVariety[]>>(
      "/crop-varieties"
    );
    return data.data;
  },

  getActiveCropVarieties: async (): Promise<CropVariety[]> => {
    const { data } = await apiClient.get<ApiResponse<CropVariety[]>>(
      "/crop-varieties",
      { params: { activeOnly: true } }
    );
    return data.data;
  },

  getCropVarietiesByCropType: async (
    cropTypeId: string
  ): Promise<CropVariety[]> => {
    const { data } = await apiClient.get<ApiResponse<CropVariety[]>>(
      `/crop-varieties/by-crop-type/${cropTypeId}`
    );
    return data.data;
  },

  getCropVarietyById: async (id: string): Promise<CropVariety> => {
    const { data } = await apiClient.get<ApiResponse<CropVariety>>(
      `/crop-varieties/${id}`
    );
    return data.data;
  },

  createCropVariety: async (
    payload: CreateCropVarietyDto
  ): Promise<CropVariety> => {
    const { data } = await apiClient.post<ApiResponse<CropVariety>>(
      "/crop-varieties",
      payload
    );
    return data.data;
  },

  updateCropVariety: async (
    payload: UpdateCropVarietyDto
  ): Promise<CropVariety> => {
    const { data } = await apiClient.put<ApiResponse<CropVariety>>(
      `/crop-varieties/${payload.id}`,
      payload
    );
    return data.data;
  },

  deleteCropVariety: async (id: string): Promise<void> => {
    await apiClient.delete(`/crop-varieties/${id}`);
  },
};
